import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';

import { screen } from '@/components/common/breakpoints';
import { ActionButton } from '@/components/common/Button';
import { Dropdown } from '@/components/common/Dropdown';
import { Icon } from '@/components/common/Icon';
import { zIndex } from '@/components/common/z-index';
import { useJobListContext } from '@/components/page/careers/context/JobListContext';
import { useURLQuery } from '@/lib/useURLQuery';
import { transparentize } from '@/styles/utils';

const JobFilters = () => {
  const stickyRef = useRef(null);
  const searchInputRef = useRef(null);
  const node = useRef();

  const { removeParam, removeAllParams, updateURLParams } = useURLQuery();

  const {
    searchTerm,
    setSearchTerm,
    filterDepartments,
    filterLocations,
    setFilterDepartments,
    setFilterLocations,
    departmentList,
    locationList,
    hasFiltersSet,
  } = useJobListContext();

  const [isSticky, setIsSticky] = useState(false);
  const filtersHeight = stickyRef.current
    ? window.scrollY + stickyRef.current.getBoundingClientRect().top
    : 0;

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > filtersHeight);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filtersHeight]);

  const handleClearAllBtn = (event) => {
    setFilterDepartments([]);
    setFilterLocations([]);
    handleRemoveTerm(event);
    removeAllParams();
  };

  const handleLocationChipClick = (filter) => {
    const newFilterValues = filterLocations.filter((value) => {
      return value !== filter;
    });

    setFilterLocations(newFilterValues);
    if (newFilterValues.length) {
      updateURLParams('Locations', newFilterValues);
    } else {
      removeParam('Locations');
    }
  };

  const handleDepartmentChipClick = (filter) => {
    const newFilterValues = filterDepartments.filter((value) => {
      return value !== filter;
    });

    setFilterDepartments(newFilterValues);
    if (newFilterValues.length) {
      updateURLParams('Departments', newFilterValues);
    } else {
      removeParam('Departments');
    }
  };

  const handleUpdateSearchTerm = (event) => {
    const newSearchValue = event.target.value?.toLowerCase();
    updateURLParams('q', newSearchValue);
    setSearchTerm(newSearchValue);
  };

  const handleRemoveTerm = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    }

    setSearchTerm('');
    removeParam('q');
  };

  const handleOnFormSubmit = (event) => {
    event.preventDefault();
  };

  return (
    <FilterSectionWrapper ref={stickyRef} hasFiltersSet={hasFiltersSet}>
      <FiltersContainer isSticky={isSticky}>
        <FiltersSection isSticky={isSticky}>
          <Form id="jobSearchForm" onSubmit={handleOnFormSubmit}>
            <StyledIcon
              icon="Search"
              size="24px"
              colour="--icon-inverse-cool-subtle"
            />
            <SearchInput
              type="search"
              ref={searchInputRef}
              onChange={handleUpdateSearchTerm}
              value={searchTerm}
              placeholder="Search jobs"
              name="q"
              id="searchJobs"
              title="Search jobs by job title"
            />
            {searchTerm && (
              <RemoveButton
                type="button"
                onClick={handleRemoveTerm}
                title="Remove search term"
              >
                <Icon icon="Close" size="11px" />
              </RemoveButton>
            )}
          </Form>
          <DropdownContainer ref={node} isSticky={isSticky}>
            <Dropdown
              label="Locations"
              options={locationList}
              node={node}
              selectedOptions={filterLocations}
              onChange={setFilterLocations}
              iconName="LocationPin"
              isSticky={isSticky}
              showAppliedCount
            />
            <Dropdown
              label="Departments"
              options={departmentList}
              node={node}
              selectedOptions={filterDepartments}
              onChange={setFilterDepartments}
              iconName="House"
              isSticky={isSticky}
              showAppliedCount
            />
          </DropdownContainer>
        </FiltersSection>
      </FiltersContainer>
      {hasFiltersSet && (
        <FiltersActionBtns>
          <FilterChipsContainer>
            {filterLocations.map((filter) => (
              <ActionButton
                key={filter.replace(' ', '-')}
                onClick={() => handleLocationChipClick(filter)}
                backgroundOfParent="white"
              >
                <span>{filter}</span>
                <ActionButtonCloseContainer>
                  <AiOutlineClose size={12} />
                </ActionButtonCloseContainer>
              </ActionButton>
            ))}
            {filterDepartments.map((filter) => (
              <ActionButton
                key={filter.replace(' ', '-')}
                onClick={() => handleDepartmentChipClick(filter)}
                backgroundOfParent="white"
              >
                <span>{filter}</span>
                <ActionButtonCloseContainer>
                  <AiOutlineClose size={12} />
                </ActionButtonCloseContainer>
              </ActionButton>
            ))}
          </FilterChipsContainer>
          <ClearAllContainer>
            <ActionButton
              backgroundOfParent="deep-space"
              onClick={handleClearAllBtn}
            >
              Clear all
            </ActionButton>
          </ClearAllContainer>
        </FiltersActionBtns>
      )}
    </FilterSectionWrapper>
  );
};

const DropdownContainer = styled.div<{ isSticky: boolean }>`
  display: ${(props) => (props.isSticky ? 'grid' : 'block')};
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-50);
  position: relative;
  flex-direction: row;
  width: 100%;
  max-width: 836px;
  margin-top: var(--spacing-250);

  ${screen.md} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-250);
  }
`;

const FilterSectionWrapper = styled.div<{ hasFiltersSet: boolean }>`
  min-height: 116px;
  width: 100%;
  z-index: 1;
  overflow: visible;
  display: block;

  ${screen.sm} {
    max-width: 626px;
  }

  ${screen.md} {
    max-width: 836px;
    min-height: ${(props) => (props.hasFiltersSet ? '184px' : '116px')};
  }
`;

const FiltersContainer = styled.div<{ isSticky: boolean }>`
  position: ${(props) => (props.isSticky ? 'fixed' : 'unset')};
  top: 0;
  left: ${(props) => (props.isSticky ? '50%' : '0')};
  transform: ${(props) => (props.isSticky ? 'translateX(-50%)' : 'none')};
  padding: ${(props) =>
    props.isSticky
      ? 'calc(var(--full-navbar-height) - var(--spacing-50)) var(--layout-gutter) var(--spacing-x-small)'
      : '0 var(--layout-gutter)'};
  width: ${(props) => (props.isSticky ? '100vw' : '100%')};
  box-shadow: ${(props) =>
    props.isSticky ? '0px 4px 8px -4px rgb(0 0 0 / 12%)' : 'none'};
  background-color: var(--surface-warm-base);
  transition:
    top,
    background-color 0.4s ease;
  z-index: ${zIndex.jobsStickyScroll};

  max-width: ${(props) =>
    props.isSticky ? '100%' : 'var(--desktop-max-width)'};
  will-change: position, top, left, box-shadow, width, transform, padding,
    max-width, height;

  ${screen.sm} {
    width: ${(props) => (props.isSticky ? '100vw' : '626px')};
  }

  ${screen.md} {
    padding: ${(props) =>
      props.isSticky
        ? 'calc(var(--full-navbar-height) + var(--spacing-x-small)) calc(50vw - 836px) var(--spacing-small)'
        : '0'};

    width: ${(props) => (props.isSticky ? '100vw' : '836px')};
  }
`;

const FiltersActionBtns = styled.div`
  padding-top: var(--spacing-medium);
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const FiltersSection = styled.div<{ isSticky: boolean }>`
  gap: var(--spacing-150);
  margin: 0 auto;
  display: ${(props) => (props.isSticky ? 'grid' : 'block')};

  grid-template-columns: 3fr 1fr;

  > div {
    margin-top: ${(props) => (props.isSticky ? '0' : 'var(--spacing-small)')};
  }

  ${screen.sm} {
    width: 626px;
  }

  ${screen.md} {
    gap: var(--spacing-medium);
    width: 836px;
    grid-template-columns: 1fr 2fr;
  }
`;

const FilterChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xxx-small);
`;

const ClearAllContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Form = styled.form`
  display: block;
  width: 100%;
  height: 48px;
  max-width: 836px;
  position: relative;

  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
    display: none;
  }
`;

const SearchInput = styled.input`
  height: 48px;
  width: 100%;
  border: 1px solid ${transparentize('--border-cool-subtle', 50)};
  color: var(--text-strong);
  text-indent: var(--spacing-700);
  border-radius: var(--radius-full);
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-x-small);

  ::placeholder {
    color: var(--text-warm-subtle);
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  position: absolute;
  right: var(--spacing-x-small);
  top: 50%;
  transform: translateY(-50%);
`;

const ActionButtonCloseContainer = styled.span`
  margin-left: var(--spacing-x-small);
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 11px;
  left: 27px;
`;

export { JobFilters };
